/**
 * §H3 zonal — "raster select (access-filtered) · zones select (Regions/Countries/Communes/
 * AOI) · stat checkboxes (mean,min,max,std)"; result "top-10 table by mean (+ `n zones
 * computed` note), `Style zones by mean` … CSV full rows".
 *
 * The raster picker excludes class rasters (`continuousRasters`): §H3 refuses one, and —
 * because §E7 validates inputs and geometry at submit and leaves the rest to the tool — the
 * refusal would arrive as a *failed job* rather than as a 422 on the button. A picker that
 * cannot offer the mistake is better than an error that explains it.
 *
 * The AOI branch answers **one row with `unit_id: null`** and no result layer (§1a rule 6:
 * no AOI-derived number is ever catalogued). The panel therefore reads the row's own
 * `unit_id` rather than the zones select to decide which of the two tables to draw, and
 * names the drawn area itself instead of printing the server's English "Drawn area".
 *
 * **The zone is derived, not stored.** The map's `#btnDraw` sits right beside the dock and
 * clearing the AOI while this form is open takes the `aoi` option out from under a reader
 * who had picked it. Storing `'aoi'` would then render a blank select and — worse — submit
 * `zone_level: Number('aoi')`, which serialises as `null`, which `serializers.zone_level`
 * reads as "absent" and answers with the default region-level run: the wrong extent, with
 * no error anywhere (NOTES #638). Falling back to the region level in the select itself
 * means what is on screen is what is submitted.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { aoiRing, useToolRun, type ZonalResult, type ZonalRow } from '../../../lib/analysis';
import { fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { selectScopeParent, useAppStore } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import { downloadCsv, toCsv } from '../csv';
import { continuousRasters, firstAvailable, useAnalysisLayers } from '../pickers';

/** §H3's four statistics, in the order the mockup checked them (std off). */
export const ZONAL_STATS = ['mean', 'min', 'max', 'std'] as const;
export const DEFAULT_STATS = ['mean', 'min', 'max'] as const;

/** How many rows §H3's panel prints; the CSV carries all of them. */
export const TOP_N = 10;

const ZONE_LEVELS = [
  { value: '1', key: 'indicators.levelRegion' },
  { value: '0', key: 'indicators.levelCountry' },
  { value: '2', key: 'indicators.levelCommune' },
];

/** The zone the select falls back to — first in `ZONE_LEVELS`, and §H3's own default. */
export const DEFAULT_ZONE = ZONE_LEVELS[0]!.value;

/** The zones option that is not a level: the reader's drawn area. */
export const AOI_ZONE = 'aoi';

const num = (row: ZonalRow, stat: string): number | null => {
  const v = row[stat];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

export function Zonal({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const parent = useAppStore((s) => selectScopeParent(s));
  const aoi = useAppStore((s) => s.aoi);
  const activeRaster = useAppStore((s) => s.layersOn);
  const addResultLayer = useAppStore((s) => s.addResultLayer);

  const layers = useAnalysisLayers();
  const rasters = useMemo(() => continuousRasters(layers), [layers]);
  const [layerId, setLayerId] = useState('');
  const [zone, setZone] = useState(DEFAULT_ZONE);
  const [stats, setStats] = useState<string[]>([...DEFAULT_STATS]);
  const run = useToolRun<ZonalResult>('zonal');

  // The select's value is derived, not stored on first render: the catalogue arrives a tick
  // after the form does, and a `useEffect` that seeded state would render one frame of an
  // empty select and submit `layer_id: ''` if the reader was quick.
  const currentLayer = firstAvailable(
    rasters,
    layerId,
    [...activeRaster].find((id) => rasters.some((l) => l.id === id)),
  );

  // The AOI can go while the form is open — see the module header.
  const currentZone = zone === AOI_ZONE && !aoi ? DEFAULT_ZONE : zone;

  const toggleStat = (stat: string) =>
    setStats((current) =>
      current.includes(stat) ? current.filter((s) => s !== stat) : [...current, stat],
    );

  const submit = () => {
    if (!currentLayer) {
      toast(t('analysis.noLayer'));
      return;
    }
    if (!stats.length) {
      toast(t('analysis.zonal.noStats'));
      return;
    }
    // The order the checkboxes are ticked in must not be a different cache entry: §E7
    // sorts stat lists when it canonicalises, and sending them sorted keeps the two
    // readings identical.
    const ordered = ZONAL_STATS.filter((s) => stats.includes(s));
    run.run(
      currentZone === AOI_ZONE && aoi
        ? { layer_id: currentLayer, aoi_polygon: aoiRing(aoi.pts), stats: ordered }
        : {
            layer_id: currentLayer,
            zone_level: Number(currentZone),
            stats: ordered,
            ...(country === 'ALL' ? {} : { country }),
            ...(parent ? { parent } : {}),
          },
    );
  };

  const result = run.result;
  const isAoiAnswer = Boolean(result && result.rows.length === 1 && result.rows[0]?.unit_id === null);
  const ranked = useMemo(() => {
    if (!result) return [];
    return [...result.rows].sort((a, b) => (num(b, 'mean') ?? -Infinity) - (num(a, 'mean') ?? -Infinity));
  }, [result]);

  const styleZones = () => {
    const spec = result?.result_layers?.[0];
    if (!spec) return;
    addResultLayer(spec, {
      jobId: run.jobId ?? spec.id,
      tool: 'zonal',
      label: t('analysis.zonal.layerLabel', { layer: result?.layer.name ?? '' }),
    });
    toast(t('analysis.zonal.styled'));
  };

  const exportCsv = () => {
    if (!result) return;
    const header = [t('analysis.csv.zone'), t('analysis.csv.country'), ...result.stats, 'pixels'];
    const rows = result.rows.map((row) => [
      row.unit_id === null ? t('analysis.aoiName') : row.name,
      row.country ?? '',
      ...result.stats.map((s) => num(row, s)),
      row.pixels,
    ]);
    downloadCsv('zonal_stats.csv', toCsv(header, rows));
  };

  return (
    <ToolFrame
      tool="zonal"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox
              title={
                isAoiAnswer
                  ? t('analysis.zonal.titleAoi', { layer: result.layer.name })
                  : t('analysis.zonal.title', { layer: result.layer.name })
              }
              scroll
            >
              <table className="mini">
                <thead>
                  <tr>
                    <th>{isAoiAnswer ? t('analysis.csv.stat') : t('analysis.csv.zone')}</th>
                    {isAoiAnswer ? (
                      <th className="num">{t('analysis.csv.value')}</th>
                    ) : (
                      result.stats.map((s) => (
                        <th key={s} className="num">
                          {t(`analysis.zonal.stat.${s}`, s)}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isAoiAnswer
                    ? result.stats.map((s) => (
                        <tr key={s}>
                          <td>{t(`analysis.zonal.stat.${s}`, s)}</td>
                          <td className="num">{fmtNum(num(result.rows[0] as ZonalRow, s), 2)}</td>
                        </tr>
                      ))
                    : ranked.slice(0, TOP_N).map((row) => (
                        <tr key={row.unit_id ?? row.name}>
                          <td>
                            {row.name} <small style={{ color: 'var(--ink-3)' }}>{row.country ?? ''}</small>
                          </td>
                          {result.stats.map((s) => (
                            <td key={s} className="num">
                              {fmtNum(num(row, s), 2)}
                            </td>
                          ))}
                        </tr>
                      ))}
                </tbody>
              </table>
            </ResultBox>

            <ResultNote>
              {isAoiAnswer
                ? t('analysis.zonal.aoiNote', {
                    resolution: result.resolution_m ? Math.round(result.resolution_m) : '-',
                  })
                : t('analysis.zonal.count', {
                    n: fmtNum(result.n_zones, 0),
                    top: Math.min(TOP_N, result.rows.length),
                  })}
            </ResultNote>

            <div className="ad-actions">
              {result.result_layers.length ? (
                <button type="button" className="btn primary sm" id="zMap" onClick={styleZones}>
                  <Icon name="map" size={12} /> {t('analysis.zonal.style')}
                </button>
              ) : null}
              <button type="button" className="btn ghost sm" id="zCsv" onClick={exportCsv}>
                <Icon name="download" size={12} /> {t('analysis.csv.button')}
              </button>
            </div>
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="zRas">
        {t('analysis.zonal.raster')}
      </label>
      <select className="std" id="zRas" value={currentLayer} onChange={(e) => setLayerId(e.target.value)}>
        {rasters.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
        {rasters.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
      </select>

      <label className="fld" htmlFor="zZone">
        {t('analysis.zonal.zones')}
      </label>
      <select
        className="std"
        id="zZone"
        value={currentZone}
        onChange={(e) => setZone(e.target.value)}
      >
        {ZONE_LEVELS.map((z) => (
          <option key={z.value} value={z.value}>
            {t(z.key)}
          </option>
        ))}
        {aoi ? <option value={AOI_ZONE}>{t('analysis.aoiName')}</option> : null}
      </select>

      <label className="fld">{t('analysis.zonal.stats')}</label>
      <div className="ad-checks">
        {ZONAL_STATS.map((s) => (
          <label key={s}>
            <input type="checkbox" checked={stats.includes(s)} onChange={() => toggleStat(s)} />
            {t(`analysis.zonal.stat.${s}`, s)}
          </label>
        ))}
      </div>

      <button
        type="button"
        className="btn primary ad-run"
        id="zRun"
        disabled={run.phase === 'running'}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.zonal.run')}
      </button>
    </ToolFrame>
  );
}

export default Zonal;
